/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useState } from 'react';

import { TemporaryDrawer, breakpoints } from '@coursera/cds-core';

import type { SaveNotificationType } from 'bundles/author-assignment-grading/constants/SaveNotificationTypes';
import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import SaveNotification from 'bundles/grading/components/staff-graded-assignments/item-setting-exceptions/SaveNotification';
import type { Team } from 'bundles/grading/components/staff-graded-assignments/types/SGAGradingTypes';
import { generateLegacyTeam } from 'bundles/grading/utils/submitterUtils';

// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import ActionsContainer from './ActionsContainer';
import ContentContainer from './ContentContainer';

const styles = {
  container: css`
    /* allow absolute positioned headingGroup to respect scrollbar */
    position: relative;
  `,
  headingGroup: css`
    ${breakpoints.up('sm')} {
      padding: 0 var(--cds-spacing-600);
    }

    position: absolute;
    right: 0;
    z-index: 2;
  `,
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

// Component for Item Setting Exceptions Drawer
// NOTE: To dev test without making temporary code changes, use: yarn serve:hot prod
//       In legacy grader, the AvailabilityPicker and DeadlinePicker fail on snowpack
//       More details: https://coursera.atlassian.net/browse/GRADE-1598
const ItemSettingExceptionsDrawer = ({ isOpen, onClose }: Props) => {
  const { submissionData } = useContext(SGAPageContext);
  const { HeadingGroup } = TemporaryDrawer;

  const [notificationType, setNotificationType] = useState<SaveNotificationType | null>(null);

  if (!submissionData.data?.submitter) {
    return null;
  }

  const legacyTeam = submissionData.data.isTeamAssignment
    ? generateLegacyTeam(submissionData.data.submitter as Team)
    : undefined;

  return (
    <TemporaryDrawer
      open={isOpen}
      anchor="right"
      sticky="actions"
      onClose={onClose}
      aria-describedby="quick-grader-item-setting-exceptions-content"
      data-testid="quick-grader-item-setting-exceptions-drawer"
    >
      {/* Empty heading group to render close button */}
      <div css={styles.container}>
        <HeadingGroup css={styles.headingGroup}>
          <React.Fragment />
        </HeadingGroup>
      </div>
      {notificationType && (
        <SaveNotification notificationType={notificationType} onDismiss={() => setNotificationType(null)} />
      )}
      <ContentContainer legacyTeam={legacyTeam} />
      <ActionsContainer legacyTeam={legacyTeam} onClose={onClose} setNotificationType={setNotificationType} />
    </TemporaryDrawer>
  );
};

export default ItemSettingExceptionsDrawer;
